import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Typed from "typed.js";
import bitcoinIcon from "../../images/front/bitcoin-hero.svg";
import blocksIcon from "../../images/front/building-blocks.svg";
import mobileBitcoin from "../../images/front/bitcoin-mobile.svg";
import mobileBlocks from "../../images/front/blocks-mobile.svg";
import uniswapLogo from "../../images/front/uniswap-white.svg";
import sushiLogo from "../../images/front/sushi-hero.svg";
import galxeLogo from "../../images/front/galxe-hero.svg";
import ledgerLogo from "../../images/front/ledger-hero.svg";
import metamaskLogo from "../../images/front/metamask-hero.png";
import mocLogo from "../../images/front/moc-hero.png";
import beefyLogo from "../../images/front/beefy-hero.svg";
import graphLogo from "../../images/front/thegraph-hero.svg";
import alchemyLogo from "../../images/front/alchemy-hero.svg";
import layerzeroLogo from "../../images/front/layerzero3.svg";
import ImageSlider from "./ImageSlider";
import AddRootstockButton from "./AddRootstockButton";

const NewIntro = (props) => {
  const data = props.data || {};
  const title1 = data.title1;
  const title2 = data.title2;
  const subtitle = data.subtitle;
  const trustedtext = data.trustedtext;
  const typeSpeed = data.typeSpeed;

  const logos = [
    { src: uniswapLogo, alt: "Uniswap logo" },
    { src: sushiLogo, alt: "SushiSwap logo" },
    { src: galxeLogo, alt: "Galxe logo" },
    { src: ledgerLogo, alt: "Ledger logo" },
    { src: metamaskLogo, alt: "MetaMask logo" },
    { src: mocLogo, alt: "MOC logo" },
    { src: beefyLogo, alt: "Beefy Finance logo" },
    { src: graphLogo, alt: "The Graph logo" },
    { src: alchemyLogo, alt: "Alchemy logo" },
    { src: layerzeroLogo, alt: "LayerZero logo" },
  ];

  // Refs
  const bitcoinRef = useRef(null);
  const redefinedRef = useRef(null);
  const bitcoinIconRef = useRef(null);
  const mobileBitcoinIconRef = useRef(null);
  const blocksIconRef = useRef(null);
  const mobileBlocksIconRef = useRef(null);
  const contentRef = useRef(null);

  const typed1Ref = useRef(null);
  const typed2Ref = useRef(null);
  const didInit = useRef(false); // guard StrictMode double-run

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    if (!bitcoinRef.current || !redefinedRef.current) return;

    // If titles are missing, render statically and reveal UI
    if (!title1 || !title2) {
      if (bitcoinRef.current) {
        bitcoinRef.current.textContent = title1 || "";
        bitcoinRef.current.classList.remove("opacity-0");
      }
      if (redefinedRef.current) {
        redefinedRef.current.textContent = title2 || "";
        redefinedRef.current.classList.remove("opacity-0");
      }
      bitcoinIconRef.current?.classList.remove("opacity-0");
      mobileBitcoinIconRef.current?.classList.remove("opacity-0");
      blocksIconRef.current?.classList.remove("opacity-0");
      mobileBlocksIconRef.current?.classList.remove("opacity-0");
      contentRef.current?.classList.remove("opacity-0");
      return;
    }

    // Parse speed; keep 0 if provided; fallback only when NaN
    const parsed = Number(typeSpeed);
    const speed = Number.isFinite(parsed) ? parsed : 30;

    // Ensure spans are empty before Typed writes into them
    bitcoinRef.current.textContent = "";
    redefinedRef.current.textContent = "";

    const baseOpts = {
      typeSpeed: speed,
      showCursor: false,
      backSpeed: 0,
      backDelay: 0,
      loop: false,
      smartBackspace: false,
    };

    typed1Ref.current = new Typed(bitcoinRef.current, {
      ...baseOpts,
      strings: [title1],
      onBegin: () => bitcoinRef.current?.classList.remove("opacity-0"),
      onComplete: () => {
        typed2Ref.current = new Typed(redefinedRef.current, {
          ...baseOpts,
          strings: [title2],
          onBegin: () => redefinedRef.current?.classList.remove("opacity-0"),
          onComplete: () => {
            bitcoinIconRef.current?.classList.remove("opacity-0");
            mobileBitcoinIconRef.current?.classList.remove("opacity-0");
            blocksIconRef.current?.classList.remove("opacity-0");
            mobileBlocksIconRef.current?.classList.remove("opacity-0");
            contentRef.current?.classList.remove("opacity-0");
          },
        });
      },
    });

    return () => {
      typed1Ref.current?.destroy();
      typed2Ref.current?.destroy();
    };
    // empty deps on purpose; we want a single init (matches your other components)
  }, []);

  const subtitleLines = (subtitle || "").split("|").filter(Boolean);

  return (
    <section className="new-hero-section pt-14-rem pb-lg-16 pb-11">
      <div className="container">
        <div className="hero-header">
          <div className="header-wrapper">
            <img
              ref={bitcoinIconRef}
              src={bitcoinIcon}
              alt="Bitcoin illustration"
              className="bitcoin-icon hidden-mobile opacity-0 transition-opacity"
            />
            <img
              ref={mobileBitcoinIconRef}
              src={mobileBitcoin}
              alt="Bitcoin illustration"
              className="bitcoin-icon hidden-desktop opacity-0 transition-opacity"
            />

            <h1 className="hero-title">
              {/* Leave spans EMPTY; Typed.js controls the text */}
              <span
                ref={bitcoinRef}
                className="zg-text-bg bg-orange text-black mb-2 mb-md-3 me-1 me-md-2 opacity-0"
              />
              <br />
              <span
                ref={redefinedRef}
                className="zg-text-bg bg-white text-black me-1 me-md-2 opacity-0"
              />
            </h1>

            <img
              ref={blocksIconRef}
              src={blocksIcon}
              alt="Icon"
              className="icon-right hidden-mobile opacity-0 transition-opacity"
            />
            <img
              ref={mobileBlocksIconRef}
              src={mobileBlocks}
              alt="Icon"
              className="icon-right hidden-desktop opacity-0 transition-opacity"
            />
          </div>
        </div>

        <div ref={contentRef} className="opacity-0 transition-opacity">
          {subtitleLines.length > 0 && (
            <div className="d-flex justify-content-center">
              <p className="hero-subtitle">
                {subtitleLines.map((line, i) => (
                  <React.Fragment key={`${line}-${i}`}>
                    {line}
                    {i < subtitleLines.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </p>
            </div>
          )}

          <AddRootstockButton />

          {trustedtext ? <p className="trusted-text">{trustedtext}</p> : null}
          <ImageSlider images={logos} />
        </div>
      </div>
    </section>
  );
};

NewIntro.propTypes = {
  data: PropTypes.shape({
    title1: PropTypes.string,
    title2: PropTypes.string,
    subtitle: PropTypes.string,
    trustedtext: PropTypes.string,
    typeSpeed: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

export default NewIntro;
